import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import classNames from "classnames";
import { usePrevious } from "@ajayvikas/react-common";
export const Tabs = ({ selectedIndex = 0, onSelect, remountKey: trigger, children, style, sticky }) => {
    const [top, setTop] = React.useState(0);
    const ref = React.useRef(null);
    const loadedTabs = React.useRef([]);
    const prevTrigger = usePrevious(trigger);
    if (prevTrigger !== trigger) {
        loadedTabs.current = [];
    }
    let headers;
    let contents;
    let seperator;
    React.Children.forEach(children, (child, index) => {
        if (child.type === Tabs.TabHeaders)
            headers = child;
        if (child.type === Tabs.TabContents)
            contents = child;
        if (child.type === Tabs.Seperator)
            seperator = child;
    });
    React.useEffect(() => {
        if (!ref.current)
            return;
        const rect = ref.current.getBoundingClientRect();
        setTop(rect.top);
    }, []);
    const onTabSelected = (e, index) => {
        if (onSelect)
            onSelect(index);
        e.preventDefault();
    };
    if (!headers)
        return null;
    const position = sticky ? "sticky" : undefined;
    return _jsxs(React.Fragment, { children: [_jsx("ul", { ref: ref, className: "nav nav-tabs", style: sticky ? { ...style, top, position } : style, children: React.Children.toArray(headers.props.children)
                    .filter(c => !!c)
                    .map((c, index) => _jsx("li", { children: _jsx("a", { href: "", className: classNames("nav-link", index === selectedIndex ? "active" : ""), onClick: e => onTabSelected(e, index), children: c }) }, index)) }), seperator, !!contents ? React.Children.toArray(contents.props.children)
                .filter(c => !!c)
                .map((tc, index) => {
                const { style: propStyle, className, async } = tc.props;
                const key = trigger ? `${trigger}-${index}` : index;
                const newContents = React.cloneElement(tc, { index, selectedIndex });
                if (index === selectedIndex) {
                    loadedTabs.current[index] = true;
                    return _jsx("div", { "data-key": key, style: propStyle, className: classNames("tab-content", className), children: newContents }, key);
                }
                else {
                    if (async && loadedTabs.current[index]) {
                        return _jsx("div", { "data-key": key, style: propStyle, className: classNames("tab-content", "d-none", className), children: newContents }, key);
                    }
                    else {
                        loadedTabs.current[index] = false;
                        return null;
                    }
                }
            }) : null] });
};
Tabs.propTypes = {
    children: (props, propName, componentName) => {
        const prop = props[propName];
        let headers;
        let contents;
        // let seperator: React.ReactElement<any> | undefined;
        React.Children.forEach(prop, (child, index) => {
            if (child.type === Tabs.TabHeaders)
                headers = child;
            if (child.type === Tabs.TabContents)
                contents = child;
            // if (child.type === Tabs.Seperator) seperator = child;
        });
        if (!headers)
            return new Error("Headers are not defined");
        // if (!contents) return new Error("Contents are not defined");
        if (headers.type !== Tabs.TabHeaders)
            return new Error("Headers are not defined");
        if (contents) {
            if (contents.type !== Tabs.TabContents)
                return new Error("Contents are not defined");
            if (headers.props.children.length !== contents.props.children.length)
                return new Error("Different number of Tab Header and Tab Contents");
        }
        return null;
    }
};
const Seperator = ({ children }) => {
    return _jsx(React.Fragment, { children: children });
};
const TabHeaders = (props) => {
    return _jsx(React.Fragment, { children: props.children });
};
TabHeaders.propTypes = {
    children: (props, propName, componentName) => {
        const prop = props[propName];
        let error = null;
        React.Children.forEach(prop, (child) => {
            if (child !== null && child.type !== Tabs.TabHeader) {
                error = new Error(`${componentName} children should be of type "Tabs.TabHeader"`);
            }
        });
        return error;
    }
};
const TabContents = (props) => {
    return _jsx(React.Fragment, { children: props.children });
};
TabContents.propTypes = {
    children: (props, propName, componentName) => {
        const prop = props[propName];
        let error = null;
        React.Children.forEach(prop, (child) => {
            if (child !== null && child.type !== Tabs.TabContent) {
                error = new Error(`${componentName} children should be of type "Tabs.TabContent"`);
            }
        });
        return error;
    }
};
const TabHeader = (props) => {
    return _jsx(React.Fragment, { children: props.children });
};
//TabContent declare a different set of properties for public and private consumption
//The public properties are consumed by the main tab component, and it clones and calles
//the TabContent with the private properties.
const TabContent = (props) => {
    const { index, selectedIndex, children } = props;
    const oldProps = React.useRef(null);
    if (!children)
        return null;
    if (selectedIndex === index) {
        oldProps.current = children.props;
        return _jsx(React.Fragment, { children: children });
    }
    else {
        if (!React.isValidElement(children))
            return _jsx(React.Fragment, { children: "children" });
        const clone = React.cloneElement(children, oldProps.current);
        return _jsx(React.Fragment, { children: clone });
    }
};
Tabs.TabHeaders = TabHeaders;
Tabs.TabHeader = TabHeader;
Tabs.Seperator = Seperator;
Tabs.TabContents = TabContents;
Tabs.TabContent = TabContent;
